/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import Grid from '@material-ui/core/Grid';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import useDeviceShippingInfo from '@ion/query/src/hooks/useDeviceShippingInfo';
import {useDeviceViewRefresh} from '../../hooks/useDeviceViewRefresh';
import DeviceDetailsGridItem from './DeviceDetailsGridItem';

export interface ShippingWarrantyTabType {
  tabIndex: Number;
  deviceIdentifier: string;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  formSection : {
    padding : '32px 0px'
  },
  formTitle : {
    ...dlsAlt('typography', 'cta3'),
    fontWeight : 500,
    padding    : '0px 16px 0px'
  },
  leftContainer : {
    flex            : 1,
    backgroundColor : dlsColor('white'),
    marginRight     : '20px'
  },
  rightContainer : {
    flex            : 1,
    backgroundColor : dlsColor('white'),
    margin          : '0 10px'
  },
  field : {
    height : '296px'
  },
  gridItem : {
    borderBottom : `1px solid ${dlsColor('grey6')}`,
    height       : '74px',
    paddingTop   : '10px'
  },
  headerItem : {
    ...dlsAlt('typography', 'label1'),
    fontWeight : 700,
    color      : dlsColor('grey2'),
    fontSize   : '14px',
    padding    : '5px 20px'
  },
  bodyItem : {
    ...dlsAlt('typography', 'label2'),
    color      : dlsColor('grey2'),
    fontWeight : 400,
    padding    : '5px 20px 15px'
  },
  flexStyle : {
    display : 'flex'
  }
}));

/**
 * ShippingWarrantyTab component that displays the Device data
 * for the selected Device Details tab.
 */
export const ShippingWarrantyTab = ({
  deviceIdentifier,
  tabIndex = 0
}: ShippingWarrantyTabType) => {
  const classes  = useStyles();

  const {
    shippingInfo,
    refresh: onRefreshShippingInfo
  } = useDeviceShippingInfo(deviceIdentifier, !!deviceIdentifier);

  const refreshTab = useCallback(onRefreshShippingInfo, []);

  useDeviceViewRefresh(refreshTab);

  return (
    (<div id={`detailsTab_${tabIndex}`}
      className={classes.formSection}>
      <div className={classes.formTitle}>{translate('admin-devices:view.shipping.title')}</div>
      <Grid container
        spacing={0}
        alignItems='flex-start'
        justifyContent='flex-start'>
        <DeviceDetailsGridItem classes={classes}
          mdSpacing={3}
          label={translate('admin-devices:view.shipping.shippingDate')}
          value={formatDateTime(shippingInfo?.ShipDate)} />
        <DeviceDetailsGridItem classes={classes}
          mdSpacing={3}
          label={translate('admin-devices:view.shipping.carrierTracking')}
          value={shippingInfo?.ShipTracking} />
        <DeviceDetailsGridItem classes={classes}
          mdSpacing={3}
          label={translate('admin-devices:view.shipping.poNumber')}
          value={shippingInfo?.PurchaseOrder} />
        <DeviceDetailsGridItem classes={classes}
          mdSpacing={3}
          label={translate('admin-devices:view.shipping.customer')}
          value={shippingInfo?.ShipCustomer} />
        <DeviceDetailsGridItem classes={classes}
          mdSpacing={3}
          label={translate('admin-devices:view.shipping.assignedDate')}
          value={formatDateTime(shippingInfo?.AssignedDate)} />
        <DeviceDetailsGridItem classes={classes}
          mdSpacing={3}
          label={translate('admin-devices:view.shipping.assignedBy')}
          value={shippingInfo?.AssignedBy?.replace(/\s/g, '')} />
      </Grid>
    </div>)
  );
};

export default ShippingWarrantyTab;
